import { SYSCOHADA_ACCOUNTS } from '../constants';
import type { Account, AccountSearchResult } from '../types';

/**
 * Search accounts from the user's actual chart of accounts (loaded from API).
 * Falls back to the embedded SYSCOHADA_ACCOUNTS constant only if no chart is provided.
 * 
 * @param term - Search term (min 2 chars)
 * @param userAccounts - The user's actual chart of accounts for the current fiscal year
 */
export function searchAccounts(term: string, userAccounts?: Account[]): AccountSearchResult[] {
  if (!term || term.length < 2) return [];

  const searchTerm = term.toLowerCase();

  // If user accounts are provided, search from those (preferred path)
  if (userAccounts && userAccounts.length > 0) {
    return searchFromUserAccounts(searchTerm, userAccounts);
  }

  // Fallback to embedded constants (legacy path — should not be reached in normal flow)
  return searchFromConstants(searchTerm);
}

function searchFromUserAccounts(searchTerm: string, accounts: Account[]): AccountSearchResult[] {
  const results: AccountSearchResult[] = [];

  for (const account of accounts) {
    // Only suggest postable (leaf) accounts — code length >= 6
    if (account.code.length < 6) continue;

    const matchesCode = account.code.toLowerCase().includes(searchTerm);
    const matchesName = account.name.toLowerCase().includes(searchTerm);

    if (matchesCode || matchesName) {
      results.push({
        account,
        matchType: matchesCode && matchesName ? 'both' : matchesCode ? 'code' : 'name',
        relevance: calculateRelevance(searchTerm, account.code, account.name),
      });
    }
  }

  return results.sort((a, b) => b.relevance - a.relevance).slice(0, 50);
}

function searchFromConstants(searchTerm: string): AccountSearchResult[] {
  const results: AccountSearchResult[] = [];

  Object.entries(SYSCOHADA_ACCOUNTS).forEach(([classKey, classData]) => {
    Object.entries(classData.accounts).forEach(([code, name]) => {
      const matchesCode = code.includes(searchTerm);
      const matchesName = name.toLowerCase().includes(searchTerm);

      if (matchesCode || matchesName) {
        const account: Account = {
          id: code,
          code,
          name,
          class: classKey as keyof typeof SYSCOHADA_ACCOUNTS,
          type: getAccountType(code)
        };

        results.push({
          account,
          matchType: matchesCode && matchesName ? 'both' : matchesCode ? 'code' : 'name',
          relevance: calculateRelevance(searchTerm, code, name)
        });
      }
    });
  });

  return results.sort((a, b) => b.relevance - a.relevance);
}

function getAccountType(code: string): Account['type'] {
  const firstDigit = code.charAt(0);
  switch (firstDigit) {
    case '1': return 'equity';
    case '2': return 'asset';
    case '3': return 'asset';
    case '4': return code.startsWith('41') ? 'asset' : 'liability';
    case '5': return 'asset';
    case '6': return 'expense';
    case '7': return 'revenue';
    default: return 'asset';
  }
}

function calculateRelevance(term: string, code: string, name: string): number {
  let score = 0;
  
  // Exact code match gets highest priority
  if (code === term) score += 100;
  // Code starts with term
  else if (code.startsWith(term)) score += 75;
  // Code contains term
  else if (code.includes(term)) score += 50;
  
  // Name exact match
  if (name.toLowerCase() === term) score += 50;
  // Name starts with term
  else if (name.toLowerCase().startsWith(term)) score += 25;
  // Name contains term
  else if (name.toLowerCase().includes(term)) score += 10;

  return score;
}